import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  VolunteerActivism as OfferingIcon,
  AccountBalance as AccountBalanceIcon,
  School as SchoolIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { donationService } from '../../services/donationService';
import { studentService } from '../../services/api';
import dayjs from 'dayjs';

const statusConfig = {
  confirmed: { label: 'Confirmado', color: 'success', icon: <CheckCircleIcon /> },
  pending: { label: 'Pendiente', color: 'warning', icon: <ErrorIcon /> },
  cancelled: { label: 'Cancelado', color: 'error', icon: <WarningIcon /> }
};

const months = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const OfferingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [donation, setDonation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [useForTuitionOpen, setUseForTuitionOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [proofDialogOpen, setProofDialogOpen] = useState(false);
  
  // Formulario para aplicar a mensualidad
  const [tuitionForm, setTuitionForm] = useState({
    student_id: '',
    month: dayjs().format('MMMM'),
    year: dayjs().year()
  });
  const [tuitionError, setTuitionError] = useState('');

  useEffect(() => {
    loadDonation();
    loadStudents();
  }, [id]);

  const loadDonation = async () => {
    try {
      setLoading(true);
      const response = await donationService.getById(parseInt(id!));
      setDonation(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar donación');
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      const response = await studentService.getAll({ per_page: 100, status: 'active' });
      setStudents(response.data.data || []);
    } catch (err) {
      console.error('Error loading students:', err);
    }
  };

  const handleConfirm = async () => {
    try {
      setActionLoading(true);
      await donationService.confirm(parseInt(id!));
      await loadDonation();
      setConfirmDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al confirmar donación');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await donationService.delete(parseInt(id!));
      navigate('/donations');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar donación');
    } finally {
      setActionLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleUseForTuition = async () => {
    if (!tuitionForm.student_id) {
      setTuitionError('Debe seleccionar un estudiante');
      return;
    }
    
    try {
      setActionLoading(true);
      await donationService.useForTuition(parseInt(id!), tuitionForm);
      await loadDonation();
      setUseForTuitionOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al aplicar donación a mensualidad');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !donation) {
    return (
      <Box m={2}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => navigate('/donations')}>
            Volver a donaciones
          </Button>
        }>
          {error || 'Donación no encontrada'}
        </Alert>
      </Box>
    );
  }

  const status = statusConfig[donation.status as keyof typeof statusConfig] || statusConfig.pending;
  const isConfirmed = donation.status === 'confirmed';
  const isPending = donation.status === 'pending';
  const isUsed = donation.used_for_payment_id !== null;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <IconButton component={Link} to="/donations" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'secondary.main' }}>
            <OfferingIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">
              {donation.donor_name || donation.russian_name || 'Donante Anónimo'}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {dayjs(donation.donation_date).format('DD [de] MMMM [de] YYYY')}
            </Typography>
          </Box>
          <Chip
            icon={status.icon}
            label={status.label}
            color={status.color as any}
            sx={{ ml: { xs: 0, sm: 2 } }}
          />
          {isUsed && (
            <Chip
              icon={<SchoolIcon />}
              label="Usado para mensualidad"
              color="info"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        
        <Box display="flex" flexWrap="wrap" gap={1}>
          <Tooltip title="Imprimir">
            <IconButton onClick={handlePrint}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
          
          {donation.receipt_url && (
            <Tooltip title="Ver comprobante">
              <IconButton onClick={() => setProofDialogOpen(true)}>
                <ReceiptIcon />
              </IconButton>
            </Tooltip>
          )}
          
          {isPending && (
            <Button
              variant="contained"
              color="success"
              onClick={() => setConfirmDialogOpen(true)}
              disabled={actionLoading}
            >
              Confirmar Donación
            </Button>
          )}
          
          {isConfirmed && !isUsed && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PaymentIcon />}
              onClick={() => setUseForTuitionOpen(true)}
              disabled={actionLoading}
            >
              Aplicar a Mensualidad
            </Button>
          )}
          
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/donations/${id}/edit`)}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Editar
          </Button>
          
          <Button
            variant="contained"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
             disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Eliminar
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Información del Donante */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información del Donante
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ width: '40%', bgcolor: 'action.hover' }}>
                        Nombre (Español)
                      </TableCell>
                      <TableCell>
                        {donation.donor_name || 'No especificado'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Nombre (Ruso)
                      </TableCell>
                      <TableCell>
                        {donation.russian_name || 'Не указано'}
                      </TableCell>
                    </TableRow>
                    {donation.parent && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Padre Registrado
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => navigate(`/parents/${donation.parent.id}`)}
                          >
                            {donation.parent.spanish_name || donation.parent.russian_name}
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Detalles de la Donación */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detalles de la Donación
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableBody>
                    {/*<TableRow>
                      <TableCell component="th" sx={{ width: '40%', bgcolor: 'action.hover' }}>
                        Fecha
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {dayjs(donation.donation_date).format('D [de] MMMM [de] YYYY')}
                        </Box>
                      </TableCell>
                    </TableRow>
                    */}
                    <TableRow>
                        <TableCell component="th" sx={{ width: '40%', bgcolor: 'action.hover' }}>
                            Método de Pago
                        </TableCell>
                        <TableCell>
                            {donation.donation_type === 'cash' && 'Efectivo'}
                            {donation.donation_type === 'bank_transfer' && 'Transferencia Bancaria'}
                            {donation.donation_type === 'check' && 'Cheque'}
                            {donation.donation_type === 'other' && 'Otro'}
                            {!donation.donation_type && 'No especificado'}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Monto
                      </TableCell>
                      <TableCell>
                        <Typography variant="h5" color="success.main" fontWeight="bold">
                          {donation.currency === 'USD' && '$'}
                          {donation.currency === 'EUR' && '€'}
                          {donation.currency === 'RUB' && '₽'}
                          {Number(donation.amount).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Referencia
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {donation.reference_number || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Estado
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={status.icon}
                          label={status.label}
                          color={status.color as any}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Información de uso (si aplica) */}
        {isUsed && donation.student && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Donación Aplicada a Mensualidad
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" sx={{ width: '40%', bgcolor: 'action.hover' }}>
                          Estudiante
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="text"
                            onClick={() => navigate(`/students/${donation.student.id}`)}
                          >
                            {donation.student.first_name} {donation.student.last_name}
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Período
                        </TableCell>
                        <TableCell>
                          {donation.monthly_payment?.month} {donation.monthly_payment?.year}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Pago Asociado
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="text"
                            onClick={() => navigate(`/monthly-payments/${donation.used_for_payment_id}`)}
                          >
                            Ver pago #{donation.used_for_payment_id}
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Notas */}
        {donation.notes && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notas
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body1">
                    {donation.notes}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Información de Seguimiento */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información de Seguimiento
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Creado por
                  </Typography>
                  <Typography>
                    {donation.created_by_user?.name || 'Sistema'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(donation.created_at).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </Grid>
                
                {donation.approved_by && (
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Confirmado por
                    </Typography>
                    <Typography>
                      {donation.approved_by_user?.name || 'Administrador'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(donation.approved_at).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                  </Grid>
                )}
                
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Última actualización
                  </Typography>
                  <Typography>
                    {dayjs(donation.updated_at).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Diálogo para confirmar donación */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirmar Donación</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Está seguro que desea confirmar esta donación?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Donante:</strong> {donation.donor_name || donation.russian_name}
            </Typography>
            <Typography variant="body2">
              <strong>Monto:</strong> {donation.currency === 'RUB' ? '₽' : donation.currency === 'USD' ? '$' : '€'} {Number(donation.amount).toLocaleString()}
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Al confirmar, esta donación se registrará en el balance general.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirm} color="success" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para aplicar a mensualidad */}
      <Dialog open={useForTuitionOpen} onClose={() => setUseForTuitionOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aplicar Donación a Mensualidad</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Seleccione el estudiante y el período al que desea aplicar esta donación.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Monto disponible:</strong> {donation.currency === 'RUB' ? '₽' : donation.currency === 'USD' ? '$' : '€'} {Number(donation.amount).toLocaleString()}
            </Typography>
          </Alert>
          
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Estudiante *</InputLabel>
            <Select
              value={tuitionForm.student_id}
              label="Estudiante *"
              onChange={(e) => setTuitionForm({...tuitionForm, student_id: e.target.value})}
            >
              {students.map((student) => (
                <MenuItem key={student.id} value={student.id}>
                  {student.first_name} {student.last_name} - {student.grade}
                </MenuItem>
              ))}
            </Select>
            {tuitionError && <FormHelperText error>{tuitionError}</FormHelperText>}
          </FormControl>
          
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Mes</InputLabel>
                <Select
                  value={tuitionForm.month}
                  label="Mes"
                  onChange={(e) => setTuitionForm({...tuitionForm, month: e.target.value})}
                >
                  {months.map((month) => (
                    <MenuItem key={month} value={month}>{month}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Año"
                value={tuitionForm.year}
                onChange={(e) => setTuitionForm({...tuitionForm, year: parseInt(e.target.value)})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUseForTuitionOpen(false)}>Cancelar</Button>
          <Button onClick={handleUseForTuition} color="primary" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Aplicar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para eliminar */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Está seguro que desea eliminar esta donación?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Donante:</strong> {donation.donor_name || donation.russian_name}
            </Typography>
            <Typography variant="body2">
              <strong>Monto:</strong> {donation.currency === 'RUB' ? '₽' : donation.currency === 'USD' ? '$' : '€'} {Number(donation.amount).toLocaleString()}
            </Typography>
          </Alert>
          {donation.status === 'confirmed' && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              Esta donación ya fue confirmada y registrada en el balance. Al eliminarla, se revertirá automáticamente.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {actionLoading ? <CircularProgress size={24} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para ver comprobante */}
      <Dialog open={proofDialogOpen} onClose={() => setProofDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Comprobante de Donación</DialogTitle>
        <DialogContent>
          {donation?.receipt_url && (
            <Box sx={{ textAlign: 'center' }}>
              {donation.receipt_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                <Box
                  component="img"
                  src={donation.receipt_url}
                  alt="Comprobante"
                  sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              ) : (
                <Box sx={{ height: '60vh' }}>
                  <iframe
                    src={donation.receipt_url}
                    title="Comprobante"
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<DownloadIcon />} href={donation?.receipt_url} download>
            Descargar
          </Button>
          <Button onClick={() => setProofDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OfferingDetail;